import { cn } from '@latitude/token-utils';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import type { ComponentRef, Ref } from 'react';
import type { AccordionContentProps } from 'types';
import { contentVariants } from 'variants/content-variants';

import { useAccordionContext } from '../AccordionContext';

/**
 * Represents the content of an accordion component.
 *
 * @component
 * @example
 * ```tsx
 * <AccordionContent>
 *   <p>This is the content of the accordion.</p>
 * </AccordionContent>
 * ```
 */
export const AccordionContent = ({
  className,
  children,
  noSpacing = false,
  ref,
  onAnimationEnd,
  ...props
}: AccordionContentProps & {
  ref?: Ref<ComponentRef<typeof AccordionPrimitive.Content>>;
}) => {
  const context = useAccordionContext();

  const handleAnimationEnd: AccordionContentProps['onAnimationEnd'] = (
    event,
  ) => {
    onAnimationEnd?.(event);
    // The open/close animations run on the content element itself, so ignore
    // animations bubbling from children. Waiting for the animation to end
    // means the layout has settled — in single mode the previous item's
    // collapse (same duration) has also finished by then.
    if (
      context?.scrollIntoViewOnOpen &&
      event.target === event.currentTarget &&
      event.currentTarget.getAttribute('data-state') === 'open'
    ) {
      event.currentTarget.parentElement?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  // The vertical padding must be given to a child of the component.
  // Otherwise the animation will get jerky. I'm unaware why the bounding rect is
  // affected if the padding is set directly on the component.
  // check https://github.com/radix-ui/primitives/blob/b32a93318cdfce383c2eec095710d35ffbd33a1c/packages/react/collapsible/src/Collapsible.tsx#L215
  return (
    <AccordionPrimitive.Content
      ref={ref}
      className={cn(
        [
          'data-[state=closed]:animate-accordion-up',
          'data-[state=open]:animate-accordion-down',
          'overflow-hidden',
        ],
        className,
      )}
      data-testid="accordion-content"
      onAnimationEnd={handleAnimationEnd}
      {...props}
    >
      <div
        className={cn({
          [contentVariants({
            size: context?.size,
            noSpacing,
          })]: true,
        })}
      >
        {children}
      </div>
    </AccordionPrimitive.Content>
  );
};

AccordionContent.displayName = AccordionPrimitive.Content.displayName;
